import React, { useState, useEffect } from 'react';
import { getAuditLogs } from '../../services/adminApiService';

const AuditLogViewer = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [limit] = useState(50);

  // Filters
  const [filters, setFilters] = useState({
    action: '',
    resourceType: '',
    adminUserId: ''
  });

  useEffect(() => {
    loadAuditLogs();
  }, [currentPage, filters]);

  async function loadAuditLogs() {
    try {
      setLoading(true);
      setError(null);

      const params = {
        limit,
        offset: currentPage * limit,
        ...(filters.action && { action: filters.action }),
        ...(filters.resourceType && { resourceType: filters.resourceType }),
        ...(filters.adminUserId && { adminUserId: filters.adminUserId })
      };

      const response = await getAuditLogs(params);
      setAuditLogs(response.auditLogs || []);
      setPagination(response.pagination);
    } catch (err) {
      console.error('Error loading audit logs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleFilterChange(e) {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(0); // Reset to first page when filters change
  }

  function handleClearFilters() {
    setFilters({
      action: '',
      resourceType: '',
      adminUserId: ''
    });
    setCurrentPage(0);
  }

  function handlePreviousPage() {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  }

  function handleNextPage() {
    if (pagination && pagination.hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  }

  function formatDetails(details) {
    if (!details) return '-';

    try {
      const parsed = typeof details === 'string' ? JSON.parse(details) : details;
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return String(details);
    }
  }

  function getActionBadgeClass(action) {
    if (action.startsWith('create')) return 'badge-success';
    if (action.startsWith('update') || action.startsWith('set')) return 'badge-info';
    if (action.startsWith('delete') || action.startsWith('deactivate') || action.startsWith('revoke')) return 'badge-danger';
    if (action.startsWith('activate') || action.startsWith('grant')) return 'badge-primary';
    return 'badge-secondary';
  }

  if (loading && auditLogs.length === 0) {
    return <div className="admin-loading">Loading audit logs...</div>;
  }

  return (
    <div className="audit-log-viewer">
      <div className="management-header">
        <h2>Audit Log</h2>
      </div>

      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label htmlFor="action">Action</label>
            <select
              id="action"
              name="action"
              value={filters.action}
              onChange={handleFilterChange}
            >
              <option value="">All Actions</option>
              <option value="create_tenant">Create Tenant</option>
              <option value="update_tenant">Update Tenant</option>
              <option value="deactivate_tenant">Deactivate Tenant</option>
              <option value="activate_tenant">Activate Tenant</option>
              <option value="create_propel_org">Create Propel Org</option>
              <option value="update_propel_org">Update Propel Org</option>
              <option value="deactivate_propel_org">Deactivate Propel Org</option>
              <option value="activate_propel_org">Activate Propel Org</option>
              <option value="set_default_propel_org">Set Default Propel Org</option>
              <option value="grant_admin_access">Grant Admin Access</option>
              <option value="revoke_admin_access">Revoke Admin Access</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="resourceType">Resource Type</label>
            <select
              id="resourceType"
              name="resourceType"
              value={filters.resourceType}
              onChange={handleFilterChange}
            >
              <option value="">All Types</option>
              <option value="tenant">Tenant</option>
              <option value="propel_org">Propel Org</option>
              <option value="admin_user">Admin User</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="adminUserId">Admin User ID</label>
            <input
              type="text"
              id="adminUserId"
              name="adminUserId"
              value={filters.adminUserId}
              onChange={handleFilterChange}
              placeholder="Filter by user ID"
            />
          </div>

          <div className="filter-actions">
            <button onClick={handleClearFilters} className="btn btn-secondary btn-small">
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="audit-log-list">
        {auditLogs.length === 0 ? (
          <div className="empty-state">No audit logs found.</div>
        ) : (
          <>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Admin User</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>Details</th>
                  <th>IP Address</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map((log) => (
                  <tr key={log.id}>
                    <td>
                      <div className="timestamp">
                        {new Date(log.timestamp).toLocaleString()}
                      </div>
                    </td>
                    <td>
                      <div className="admin-info">
                        <div>{log.admin_email}</div>
                        <code className="small-code">{log.admin_user_id}</code>
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${getActionBadgeClass(log.action)}`}>
                        {log.action.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>
                      {log.resource_type && (
                        <div className="resource-info">
                          <div className="resource-type">{log.resource_type}</div>
                          <code className="small-code">{log.resource_id}</code>
                        </div>
                      )}
                    </td>
                    <td>
                      {log.details && (
                        <details className="log-details">
                          <summary>View Details</summary>
                          <pre className="details-content">{formatDetails(log.details)}</pre>
                        </details>
                      )}
                    </td>
                    <td>
                      <code className="small-code">{log.ip_address || '-'}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {pagination && (
              <div className="pagination">
                <div className="pagination-info">
                  Showing {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
                </div>
                <div className="pagination-controls">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 0}
                    className="btn btn-secondary btn-small"
                  >
                    Previous
                  </button>
                  <span className="page-number">Page {currentPage + 1}</span>
                  <button
                    onClick={handleNextPage}
                    disabled={!pagination.hasMore}
                    className="btn btn-secondary btn-small"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AuditLogViewer;
